import { Hex } from 'viem';
import { mintPortal } from '../portal-utils';
import { IdentityInstance } from './identity-contract-module';
import { getLegacyAgent, getNewSignedMessage } from './key-derivation-utils';
import {
  encryptAndUploadKeyStore,
  updateKeyStoreData,
  getKeyStoreAddresses,
  getKeyStoreValueByAddress,
  getKeyStoreData,
} from './key-store-utils';
import { encryptUsingRSAKey, decryptUsingRSAKey } from '../crypto-utils';
import { getAuthenticatedUserNode } from '../gun-utils';
import { IPortal, TGunUserNode } from '@/types';
import { AgentInstance } from './smart-account-agent';
import { checkAndTransferOwnership } from './transfer-portal-ownership';
import {
  CheckAndTransferOwnershipSteps,
  InitializationSteps,
} from '@/components/identity-setup-modal/types';
import { captureException } from '@sentry/nextjs';
import { IRSAKeyPair, PrivateKeysJSON } from './types';
import { KeyStoreCache } from '@/db/db';
import { addKeyStoreCache } from '@/db/queries';
import * as ucans from '@ucans/ucans';
import { STORAGE_DID } from '../constants';

class KeyStore {
  ownerKeyPair: IRSAKeyPair | undefined;
  memberKeyPair: IRSAKeyPair | undefined;
  portalKeyPair: IRSAKeyPair | undefined;
  editSecret: string | undefined;
  viewSecret: string | undefined;
  gunPassword: Hex | undefined;
  portalAddress: Hex | undefined;
  isLegacyUser: boolean = false;
  gunUserNode: TGunUserNode | undefined;
  ucanToken: ucans.Ucan | undefined;

  async getKeyStore() {
    const keyStoreData = await this.getKeyStoreData();
    return keyStoreData;
  }

  async initializeKeyStore({
    addresses,
    hash,
    index,
  }: {
    addresses: Hex[];
    hash: string;
    index: number;
  }) {
    this.portalAddress = addresses[index];

    const existingKeyStoreData = await getKeyStoreData(
      this.portalAddress,
      hash
    );

    this.setKeyStoreData(existingKeyStoreData);

    await this.initializeGunUserNode(
      existingKeyStoreData.gunPassword,
      existingKeyStoreData.isLegacyUser
    );
  }

  async initializeGunUserNode(password: Hex, isLegacy: boolean) {
    try {
      const { node: userNode } =
        (await getAuthenticatedUserNode(password, isLegacy)) || {};
      this.gunUserNode = userNode;
    } catch (error) {
      captureException(error);
      console.error(error);
    }
  }

  getPortalAddress() {
    if (!this.portalAddress) throw new Error('Portal address not initialized');
    return this.portalAddress;
  }

  getEditSecret() {
    if (!this.editSecret) throw new Error('Edit secret not initialized');
    return this.editSecret;
  }

  getPortalPublicKey() {
    if (!this.portalKeyPair) throw new Error('Portal key pair not initialized');
    return this.portalKeyPair.publicKey;
  }

  getOwnerPublicKey() {
    if (!this.ownerKeyPair) throw new Error('Owner key pair not initialized');
    return this.ownerKeyPair.publicKey;
  }
  getOwnerPrivateKey() {
    if (!this.ownerKeyPair) throw new Error('Owner key pair not initialized');
    return this.ownerKeyPair.privateKey;
  }

  getGunUserNode() {
    return this.gunUserNode;
  }

  async getAuthToken() {
    if (!this.editSecret || !this.portalAddress)
      throw new Error('Edit Secret or contract address not initialized');

    if (this.ucanToken && !ucans.isExpired(this.ucanToken))
      return ucans.encode(this.ucanToken);

    const keypair = ucans.EdKeypair.fromSecretKey(this.editSecret.trim());
    this.ucanToken = await ucans.build({
      audience: STORAGE_DID,
      issuer: keypair,
      lifetimeInSeconds: 7 * 86400,
      capabilities: [
        {
          with: {
            scheme: 'storage',
            hierPart: this.portalAddress.toLowerCase(),
          },
          can: { namespace: 'file', segments: ['CREATE'] },
        },
      ],
    });

    return ucans.encode(this.ucanToken);
  }

  setKeyStoreData(keyStoreData: KeyStoreCache) {
    this.isLegacyUser = keyStoreData.isLegacyUser;
    this.ownerKeyPair = keyStoreData.ownerKeyPair;
    this.memberKeyPair = keyStoreData.memberKeyPair;
    this.portalKeyPair = keyStoreData.portalKeyPair;
    this.editSecret = keyStoreData.editSecret;
    this.viewSecret = keyStoreData.viewSecret;
    this.gunPassword = keyStoreData.gunPassword;
    this.portalAddress = keyStoreData.portalAddress;
  }

  async setupLegacyUserKeyStore(
    updateInitializationSteps: (step: InitializationSteps) => void,
    updateCheckAndTransferOwnershipSteps: (
      step: CheckAndTransferOwnershipSteps
    ) => void,
    portalKeys: IPortal
  ) {
    const walletClient = IdentityInstance.getWalletClient();
    updateInitializationSteps(InitializationSteps.CHECK_AND_TRANSFER_OWNERSHIP);

    const legacySmartAccountClient = await getLegacyAgent(walletClient);

    await checkAndTransferOwnership(
      legacySmartAccountClient,
      portalKeys.portalAddress as Hex,
      portalKeys.credentials.editSecret,
      portalKeys.credentials.viewSecret,
      updateCheckAndTransferOwnershipSteps
    );

    portalKeys.owner = AgentInstance.getAgentAddress();

    updateInitializationSteps(InitializationSteps.UPDATE_KEYSTORE);
    const keyStoreData = await encryptAndUploadKeyStore(portalKeys);

    await updateKeyStoreData(
      keyStoreData.hash,
      portalKeys.portalAddress as Hex
    );

    await addKeyStoreCache(keyStoreData);

    this.setKeyStoreData(keyStoreData);
  }

  async intialiseNewKeyStore(
    onStepComplete: (step: InitializationSteps) => void
  ) {
    onStepComplete(InitializationSteps.DEPLOY_PORTAL);
    const signedMessage = await getNewSignedMessage(
      IdentityInstance.getWalletClient()
    );

    const portalKeys = await mintPortal();

    onStepComplete(InitializationSteps.UPDATE_KEYSTORE);

    const keyStoreData = await encryptAndUploadKeyStore(portalKeys);
    await updateKeyStoreData(
      keyStoreData.hash,
      portalKeys.portalAddress as Hex
    );

    await addKeyStoreCache(keyStoreData);

    await this.initializeGunUserNode(
      signedMessage,
      KeyStoreInstance.isLegacyUser
    );

    this.setKeyStoreData(keyStoreData);
  }

  async getKeyStoreData() {
    const keyStoreAddresses = await getKeyStoreAddresses();
    if (keyStoreAddresses.length === 0) return null;

    const values = await getKeyStoreValueByAddress(keyStoreAddresses);
    if (!values) return null;

    const { hash, index } = values;

    return {
      addresses: keyStoreAddresses,
      hash,
      index,
    };
  }

  getEncryptionKey(type: 'owner' | 'member' | 'portal') {
    const keyPair = this[`${type}KeyPair`];
    if (!keyPair) throw new Error('Key pair not found');

    return keyPair.publicKey;
  }

  getDecryptionKey(type: 'owner' | 'member' | 'portal') {
    const keyPair = this[`${type}KeyPair`];
    if (!keyPair) throw new Error('Key pair not found');

    return keyPair.privateKey;
  }

  async encryptData(data: any, type: 'owner' | 'member' | 'portal') {
    const publicKey = this.getEncryptionKey(type);
    return await encryptUsingRSAKey(data, publicKey);
  }

  async decryptData(data: any, type: 'owner' | 'member' | 'portal') {
    const privateKey = this.getDecryptionKey(type);
    return await decryptUsingRSAKey(data, privateKey);
  }

  resetKeyStore() {
    this.ownerKeyPair = undefined;
    this.memberKeyPair = undefined;
    this.portalKeyPair = undefined;
    this.editSecret = undefined;
    this.viewSecret = undefined;
    this.gunPassword = undefined;
    this.portalAddress = undefined;
    this.gunUserNode = undefined;
    this.isLegacyUser = false;
  }

  getDownloadableKeys(): PrivateKeysJSON {
    return {
      portalAddress: this.portalAddress as Hex,
      ownerPublicKey: this.ownerKeyPair?.privateKey || '',
      ownerPrivateKey: this.ownerKeyPair?.privateKey || '',
      portalPrivateKey: this.portalKeyPair?.privateKey || '',
      portalPublicKey: this.portalKeyPair?.publicKey || '',
      memberPrivateKey: this.memberKeyPair?.privateKey || '',
      memberPublicKey: this.memberKeyPair?.publicKey || '',
      source: window.location.host,
    };
  }
}

export const KeyStoreInstance = new KeyStore();
